import { useMemo } from 'react';

import { DESSEM_CENARIO_LABEL } from 'helper/Relatorios/Aprovacao/config';
import { OPTIONS_POLLING_MS, OPTIONS_REFETCH_APOS_S } from 'hooks/Relatorios/Dessem/useDessemMaxData';
import { useGetDessemOptionsQuery } from 'services/api/products/relatorios';
import { DessemCenario } from 'types/Relatorios/Dessem';

export interface CenarioOpcao {
  value: DessemCenario;
  label: string;
  disponivel: boolean;
  isDefault: boolean;
}

const FALLBACK: CenarioOpcao[] = (Object.keys(DESSEM_CENARIO_LABEL) as DessemCenario[])
  .map((value) => ({
    value, label: DESSEM_CENARIO_LABEL[value], disponivel: true, isDefault: false,
  }));

/** Cenário a aplicar ao trocar de dia, ou `null` para manter o atual. */
export const cenarioParaODia = (
  opcoes: CenarioOpcao[],
  atual: DessemCenario,
): DessemCenario | null => {
  if (opcoes.find((o) => o.value === atual)?.disponivel) return null;
  const alvo = opcoes.find((o) => o.isDefault && o.disponivel) ?? opcoes.find((o) => o.disponivel);
  return alvo ? alvo.value : null;
};

export interface CenariosDoDia {
  opcoes: CenarioOpcao[];
  /** Options do dia ainda a caminho (sem `currentData` e buscando, fora de erro). */
  carregando: boolean;
}

/**
 * Radios de cenário do dia (`GET options?data_rodada=`): label, ordem e disponibilidade do BE.
 * Usa `currentData` para não mostrar os cenários do dia anterior durante o refetch, e refaz a
 * consulta ao montar (cache com mais de 60 s) e a cada 5 min, porque um cenário pode chegar ao
 * Core/ser publicado ao longo do dia (mesmas constantes do `useDessemMaxData`).
 *
 * Cai nos 3 cenários do config, todos habilitados, quando não há lista confiável: sem
 * `cenarios` (carregando, erro ou BE antigo) ou `core_disponivel === false`. Esse campo só vem na
 * resposta do admin (o BE o remove para o cliente), e com o Core fora o BE devolve `disponivel`
 * como "tem release", o que desabilitaria todos os radios de um dia ainda sem release.
 * `carregando` deixa o painel travar os radios até a lista do dia chegar.
 */
export default function useDessemCenarios(dataRodadaIso: string): CenariosDoDia {
  const {
    currentData, isFetching, isError, error,
  } = useGetDessemOptionsQuery(
    { data_rodada: dataRodadaIso },
    { refetchOnMountOrArgChange: OPTIONS_REFETCH_APOS_S, pollingInterval: OPTIONS_POLLING_MS },
  );
  // `error` cobre o retry após erro: o RTK volta a `pending` (isError false) mas mantém o erro.
  const carregando = currentData === undefined && isFetching && !isError && error === undefined;

  const opcoes = useMemo(() => {
    if (currentData?.core_disponivel === false) return FALLBACK;
    const cenarios = currentData?.cenarios?.filter((c) => c.conhecida);
    if (!cenarios?.length) return FALLBACK;
    return [...cenarios]
      .sort((a, b) => a.ordem - b.ordem)
      .map((c) => ({
        value: c.code, label: c.label, disponivel: c.disponivel, isDefault: c.is_default,
      }));
  }, [currentData]);

  return useMemo(() => ({ opcoes, carregando }), [opcoes, carregando]);
}
